import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  qrtGetGlobalMarkups, qrtSetGlobalMarkups, qrtListPricing, qrtUpsertPrice,
  qrtPricePreview, qrtListRecharges, qrtListTransactions,
} from "../../apis/qrtAdmin";
import { Pager, StatusChip, fmtDateTime, fmtMoney } from "./qrtUi";
import { isCredit, parseMeta } from "../../utils/walletTxn";

const CATEGORIES = ["marketing", "marketing_lite", "utility", "authentication", "service"];
const CATEGORY_LABELS = {
  marketing: "Marketing", marketing_lite: "Marketing Lite", utility: "Utility",
  authentication: "Authentication", service: "Service",
};

/* ---------- markups + preview ---------- */

const MarkupsSection = () => {
  const [markups, setMarkups] = useState({});
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState({ account_id: "", category: "marketing", country: "IN", result: null });

  useEffect(() => {
    qrtGetGlobalMarkups().then((res) => {
      const m = {};
      (res.data?.data || []).forEach((r) => { m[r.category] = String(r.markup); });
      setMarkups(m);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      CATEGORIES.forEach((c) => {
        if (markups[c] !== undefined && markups[c] !== "") payload[c] = Number(markups[c]);
      });
      await qrtSetGlobalMarkups(payload);
      toast.success("Global markups saved");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save");
    } finally { setBusy(false); }
  };

  const runPreview = async () => {
    if (!preview.account_id) { toast.error("Enter a user ID"); return; }
    try {
      const res = await qrtPricePreview(preview);
      setPreview((p) => ({ ...p, result: res.data?.data }));
    } catch {
      toast.error("Preview failed");
    }
  };

  return (
    <div className="qrt-card">
      <div className="qrt-card-head"><h3>Global markups (₹/message on top of base cost)</h3></div>
      <div className="qrt-card-body">
        <div className="qrt-mkgrid" style={{ maxWidth: 560 }}>
          {CATEGORIES.map((c) => (
            <div className="row" key={c}>
              <span>{CATEGORY_LABELS[c]}</span>
              <input
                type="number" step="0.01" min="0" value={markups[c] ?? ""}
                onChange={(e) => setMarkups((m) => ({ ...m, [c]: e.target.value }))}
              />
            </div>
          ))}
        </div>
        <button type="button" className="ping-btn ping-btn-green ping-btn-sm" style={{ marginTop: 10 }}
          disabled={busy} onClick={save}>
          Save markups
        </button>

        <div className="qrt-sect" style={{ marginTop: 16, marginBottom: 0 }}>
          <p className="t">Price preview (effective price for a user)</p>
          <div className="qrt-inline">
            <input type="text" placeholder="User ID" inputMode="numeric" value={preview.account_id}
              onChange={(e) => setPreview((p) => ({ ...p, account_id: e.target.value.replace(/\D/g, ""), result: null }))}
              style={{ width: 90 }} />
            <select value={preview.category}
              onChange={(e) => setPreview((p) => ({ ...p, category: e.target.value, result: null }))}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
            </select>
            <input type="text" placeholder="ISO" maxLength={4} value={preview.country}
              onChange={(e) => setPreview((p) => ({ ...p, country: e.target.value.toUpperCase(), result: null }))}
              style={{ width: 70, textTransform: "uppercase" }} />
            <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={runPreview}>Resolve</button>
            {preview.result && (
              <span style={{ fontSize: 13 }}>
                base {fmtMoney(preview.result.base_cost)} + markup {fmtMoney(preview.result.markup)} ={" "}
                <b>{fmtMoney(preview.result.price)}</b>
                <span className="sub" style={{ marginLeft: 4 }}>({preview.result.country_iso})</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- price book ---------- */

const PriceBookSection = () => {
  const [pricing, setPricing] = useState({ rows: [], total: 0 });
  const [country, setCountry] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ category: "marketing", country_iso: "", base_cost: "" });

  const load = useCallback(() => {
    qrtListPricing({
      limit: 40, offset: page * 40,
      ...(country ? { country } : {}),
      ...(category ? { category } : {}),
    }).then((res) => setPricing(res.data?.data || { rows: [], total: 0 })).catch(() => {});
  }, [page, country, category]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.country_iso || draft.base_cost === "") {
      toast.error("Country ISO and base cost required");
      return;
    }
    setBusy(true);
    try {
      await qrtUpsertPrice({ ...draft, base_cost: Number(draft.base_cost) });
      toast.success("Price row added (effective now)");
      setDraft({ category: "marketing", country_iso: "", base_cost: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to add price");
    } finally { setBusy(false); }
  };

  return (
    <div className="qrt-card">
      <div className="qrt-card-head">
        <h3>Price book (base costs, current effective)</h3>
        <input type="text" placeholder="Country ISO" maxLength={4} value={country}
          onChange={(e) => { setCountry(e.target.value.toUpperCase()); setPage(0); }}
          style={{ width: 110, textTransform: "uppercase" }} />
        <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(0); }}>
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
        </select>
      </div>

      <div className="qrt-card-head" style={{ background: "#F6FCF8" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-2)" }}>New price (effective now):</span>
        <select value={draft.category} onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
        </select>
        <input type="text" placeholder="ISO (e.g. IN / DEF)" maxLength={4} value={draft.country_iso}
          onChange={(e) => setDraft((d) => ({ ...d, country_iso: e.target.value.toUpperCase() }))}
          style={{ width: 130, textTransform: "uppercase" }} />
        <input type="number" step="0.0001" min="0" placeholder="Base cost ₹" value={draft.base_cost}
          onChange={(e) => setDraft((d) => ({ ...d, base_cost: e.target.value }))}
          style={{ width: 110 }} />
        <button type="button" className="ping-btn ping-btn-green ping-btn-sm" disabled={busy} onClick={add}>Add</button>
      </div>

      <table className="qrt-table">
        <thead>
          <tr>
            <th>Country</th>
            <th>Category</th>
            <th className="r">Base cost</th>
            <th className="r">Effective from</th>
          </tr>
        </thead>
        <tbody>
          {pricing.rows.map((r) => (
            <tr key={r.id}>
              <td className="mono" style={{ fontWeight: 700 }}>{r.country_iso}</td>
              <td>{CATEGORY_LABELS[r.category]}</td>
              <td className="r">{fmtMoney(r.base_cost)}</td>
              <td className="r sub">{fmtDateTime(r.effective_from)}</td>
            </tr>
          ))}
          {pricing.rows.length === 0 && (
            <tr><td className="empty" colSpan={4}>No price rows</td></tr>
          )}
        </tbody>
      </table>
      <Pager page={page} total={pricing.total} pageSize={40} onPage={setPage} />
    </div>
  );
};

/* ---------- recharges ---------- */

const RechargesSection = () => {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    qrtListRecharges({ limit: 25, offset: page * 25, ...(status ? { status } : {}) })
      .then((res) => setData(res.data?.data || { rows: [], total: 0 }))
      .catch(() => {});
  }, [page, status]);

  return (
    <div className="qrt-card">
      <div className="qrt-card-head">
        <h3>Razorpay recharges</h3>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
          <option value="">All</option>
          <option value="paid">Paid</option>
          <option value="created">Created</option>
          <option value="failed">Failed</option>
        </select>
      </div>
      <table className="qrt-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Order</th>
            <th className="r">Amount</th>
            <th>Status</th>
            <th className="r">Date</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.id}>
              <td>
                <div style={{ fontWeight: 600 }}>{r.user_name || `#${r.platform_account_id}`}</div>
                <div className="sub">{r.user_email}</div>
              </td>
              <td className="mono sub">
                {r.rzp_order_id}
                {r.rzp_payment_id && <div>{r.rzp_payment_id}</div>}
              </td>
              <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(r.amount)}</td>
              <td><StatusChip value={r.status} /></td>
              <td className="r sub">{fmtDateTime(r.created_at)}</td>
            </tr>
          ))}
          {data.rows.length === 0 && (
            <tr><td className="empty" colSpan={5}>No recharges yet</td></tr>
          )}
        </tbody>
      </table>
      <Pager page={page} total={data.total} pageSize={25} onPage={setPage} />
    </div>
  );
};

/* ---------- ledger (all users) ---------- */

const TransactionsSection = () => {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [type, setType] = useState("");
  const [userId, setUserId] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      qrtListTransactions({
        limit: 50, offset: page * 50,
        ...(type ? { type } : {}),
        ...(userId ? { user_id: userId } : {}),
      })
        .then((res) => setData(res.data?.data || { rows: [], total: 0 }))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [page, type, userId]);

  return (
    <div className="qrt-card">
      <div className="qrt-card-head">
        <h3>Wallet ledger (all users)</h3>
        <input type="text" placeholder="User ID" inputMode="numeric" value={userId}
          onChange={(e) => { setUserId(e.target.value.replace(/\D/g, "")); setPage(0); }}
          style={{ width: 90 }} />
        <select value={type} onChange={(e) => { setType(e.target.value); setPage(0); }}>
          <option value="">All types</option>
          <option value="recharge">Recharge</option>
          <option value="debit">Debit</option>
          <option value="refund">Refund</option>
          <option value="adjustment">Adjustment</option>
        </select>
      </div>
      <table className="qrt-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Type</th>
            <th className="r">Amount</th>
            <th className="r">Balance after</th>
            <th>Reference</th>
            <th className="r">Date</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((t) => (
            <tr key={t.id}>
              <td>
                <div>{t.user_name || `#${t.platform_account_id}`}</div>
                <div className="sub">{t.user_email}</div>
              </td>
              <td className="mono">{t.type}</td>
              <td className="r" style={{ fontWeight: 700 }}>
                {isCredit(t.type, parseMeta(t.meta), t.sign) ? "+" : "−"}{fmtMoney(t.amount)}
              </td>
              <td className="r sub">{fmtMoney(t.balance_after)}</td>
              <td className="mono sub">{t.reference_type}:{t.reference_id}</td>
              <td className="r sub">{fmtDateTime(t.created_at)}</td>
            </tr>
          ))}
          {data.rows.length === 0 && (
            <tr><td className="empty" colSpan={6}>No transactions</td></tr>
          )}
        </tbody>
      </table>
      <Pager page={page} total={data.total} pageSize={50} onPage={setPage} />
    </div>
  );
};

const QrtBilling = () => (
  <div>
    <div className="qrt-page-head">
      <div>
        <h2>Billing</h2>
        <p>Markups, price book, recharges and the platform-wide ledger</p>
      </div>
    </div>
    <MarkupsSection />
    <PriceBookSection />
    <RechargesSection />
    <TransactionsSection />
  </div>
);

export default QrtBilling;
